import { Col, DatePicker, DatePickerProps, Input, Select, Space, Table, TableProps } from "antd";
import useFetchList from "../../../hooks/useFetchList";
import useFilterOffice from "../../../hooks/useFilterOffice";
import { ListingMaskingHistoryProperties } from "../../../types/listingMaskingHistory.type";
import useAuth from "../../../hooks/useAuth";
import { EListingStatus, ListListingMaskingQuery, ListingProperties } from "../../../types/listing.type";
import { Link } from "react-router-dom";
import TextOrNotSet from "../../../components/TextOrNotSet";
import NotSet from "../../../components/NotSet";
import CustomPagination from "../../../components/CustomPagination";
import { formatYearToTime } from "../../../helpers/format";
import Filter from "../../../components/Filter";
import { SearchOutlined } from "@ant-design/icons";
import FilterOffice from "../../../components/form/FilterOffice";
import { RangePickerProps } from "antd/lib/date-picker";
import { useEffect, useState } from "react";
import moment from "moment";
import { DEFAULT_STATUS } from "./MaskingTable";

const { RangePicker } = DatePicker;

const HistoryMaskingTable: React.FC = () => {
	const { user, isAgent, isOfficeManagementOnly } = useAuth();
	const filterOfficeId = useFilterOffice();
	const [dateRange, setDateRange] = useState<any>([null, null]);
	const [maskingDateRange, setMaskingDateRange] = useState<any>([null, null]);

	const { isLoading, data, pagination, changePage, setQuery, search, setSearch, query } = useFetchList<
		ListingMaskingHistoryProperties,
		ListListingMaskingQuery
	>({
		endpoint: "listing-masking-histories",
		initialQuery: {
			filterOfficeId: isOfficeManagementOnly ? filterOfficeId : undefined,
			filterUserId: isAgent ? user.userId : undefined,
			limit: 10,
			includeData: "listing,user,employee",
		},
	});

	const _handleFilterOffice = (value: string) => {
		setQuery({
			...query,
			offset: 0,
			filterOfficeId: value,
		});
	};

	const COLUMNS: TableProps<ListingMaskingHistoryProperties>["columns"] = [
		{
			title: "LISTING ID",
			key: "listingCode",
			width: 120,
			dataIndex: ["listing", "listingCode"],
			sorter: (a: any, b: any) => a?.listing?.listingCode?.localeCompare(b?.listing?.listingCode),
			render: (listingCode: string, record: ListingProperties) => {
				return (
					<Link to={"/masking/" + record.listingId}>
						<TextOrNotSet value={listingCode?.toUpperCase()} />
					</Link>
				);
			},
		},
		{
			title: "TITLE",
			key: "title",
			dataIndex: ["listing", "title"],
			render(value: string) {
				return value ?? <NotSet />;
			},
		},
		{
			title: "LISTING AGENT",
			dataIndex: ["listing", "user", "nickName"],
			sorter: (a: any, b: any) => a?.listing?.user?.nickName.localeCompare(b?.listing?.user?.nickName),
			key: "nickName",
			render: (nickName: string) => <TextOrNotSet value={nickName?.toUpperCase()} />,
		},
		{
			title: "LISTING OFFICE",
			dataIndex: ["listing", "office", "officeName"],
			key: "officeName",
			render: (officeName: string) => <TextOrNotSet value={officeName?.toUpperCase()} />,
		},
		{
			title: "MASKING BY AGENT",
			dataIndex: ["employee", "user", "nickName"],
			sorter: (a: any, b: any) => a?.employee?.user?.nickName.localeCompare(b?.employee?.user?.nickName),
			key: "nickName",
			render: (nickName: string) => <TextOrNotSet value={nickName?.toUpperCase()} />,
		},
		{
			title: "MASKING BY OFFICE",
			dataIndex: ["employee", "office", "officeName"],
			key: "officeName",
			render: (officeName: string) => <TextOrNotSet value={officeName?.toUpperCase()} />,
		},
		{
			title: "TYPE",
			dataIndex: "type",
			key: "type",
			render: (type: string) => <TextOrNotSet value={type?.toUpperCase()} />,
		},
		{
			title: "MASKING DATE",
			dataIndex: "maskingDate",
			key: "maskingDate",
			sorter: (a: any, b: any) => moment(a.maskingDate).unix() - moment(b.maskingDate).unix(),
			render: (maskingDate: string) => <>{maskingDate ? formatYearToTime(maskingDate) : "-"}</>,
		},
		{
			title: "ACTION DATE",
			dataIndex: "createdAt",
			key: "createdAt",
			sorter: (a: any, b: any) => moment(a.createdAt).unix() - moment(b.createdAt).unix(),
			render: (createdAt: string) => <>{formatYearToTime(createdAt)}</>,
		},
	].filter(Boolean) as TableProps<ListingMaskingHistoryProperties>["columns"];

	const handleFilterActionDate = (
		value: DatePickerProps["value"] | RangePickerProps["value"],
		dateString: [string, string] | string
	) => {
		setDateRange([dateString[0], dateString[1]]);
	};

	const handleFilterMaskingDate = (
		value: DatePickerProps["value"] | RangePickerProps["value"],
		dateString: [string, string] | string
	) => {
		setMaskingDateRange([dateString[0], dateString[1]]);
	};

	useEffect(() => {
		if (dateRange[0] && dateRange[1]) {
			setQuery((prev) => ({
				...prev,
				filterCreatedAtStartAt: dateRange[0] ? moment(dateRange[0]).startOf("day").toISOString() : "",
				filterCreatedAtEndAt: dateRange[1] ? moment(dateRange[1]).endOf("day").toISOString() : "",
				offset: 0,
			}));
		} else if (dateRange[0]) {
			setQuery((prev) => ({
				...prev,
				filterCreatedAtStartAt: dateRange[0] ? moment(dateRange[0]).startOf("day").toISOString() : "",
				offset: 0,
			}));
		} else if (dateRange[1]) {
			setQuery((prev) => ({
				...prev,
				filterCreatedAtEndAt: dateRange[1] ? moment(dateRange[1]).endOf("day").toISOString() : "",
				offset: 0,
			}));
		} else {
			setQuery((prev) => ({
				...prev,
				filterCreatedAtStartAt: undefined,
				filterCreatedAtEndAt: undefined,
				offset: 0,
			}));
		}
	}, [dateRange]);

	useEffect(() => {
		if (maskingDateRange[0] && maskingDateRange[1]) {
			setQuery((prev) => ({
				...prev,
				filterMaskingDateStartAt: maskingDateRange[0]
					? moment(maskingDateRange[0]).startOf("day").toISOString()
					: "",
				filterMaskingDateEndAt: maskingDateRange[1]
					? moment(maskingDateRange[1]).endOf("day").toISOString()
					: "",
				offset: 0,
			}));
		} else if (maskingDateRange[0]) {
			setQuery((prev) => ({
				...prev,
				filterMaskingDateStartAt: maskingDateRange[0]
					? moment(maskingDateRange[0]).startOf("day").toISOString()
					: "",
				offset: 0,
			}));
		} else if (maskingDateRange[1]) {
			setQuery((prev) => ({
				...prev,
				filterMaskingDateEndAt: maskingDateRange[1]
					? moment(maskingDateRange[1]).endOf("day").toISOString()
					: "",
				offset: 0,
			}));
		} else {
			setQuery((prev) => ({
				...prev,
				filterMaskingDateStartAt: undefined,
				filterMaskingDateEndAt: undefined,
				offset: 0,
			}));
		}
	}, [maskingDateRange]);

	return (
		<Space
			direction="vertical"
			size="large"
		>
			<Filter maxWidth={"100%"}>
				<Col
					xs={{ span: 12 }}
					md={{ span: 3 }}
				>
					<Select
						virtual={true}
						onChange={(v) => {
							if (v === "all") {
								setQuery((prev) => ({
									...prev,
									filterStatusListing: DEFAULT_STATUS,
									offset: 0,
								}));
							} else {
								setQuery((prev) => ({
									...prev,
									filterStatusListing: v,
									offset: 0,
								}));
							}
						}}
						className="outer-shadow"
						placeholder="Status"
						defaultValue="all"
						options={[
							{
								label: "All Listing",
								value: "all",
							},
							{
								label: "Publish",
								value: EListingStatus.PUBLISH,
							},
							{
								label: "Unpublish",
								value: EListingStatus.UNPUBLISH,
							},
							{
								label: "Expired",
								value: EListingStatus.EXPIRED,
							},
							{
								label: "Closed",
								value: EListingStatus.CLOSED,
							},
						]}
					/>
				</Col>
				<Col
					xs={{ span: 12 }}
					md={{ span: 9 }}
				>
					<Input
						className="outer-shadow"
						placeholder="Search by Title, Listing ID or Agent Name..."
						suffix={<SearchOutlined />}
						allowClear
						onChange={(e) => setSearch(e.target.value)}
						value={search}
					/>
				</Col>
				<Col
					xs={{ span: 12 }}
					md={{ span: 6 }}
				>
					<Select
						virtual={true}
						allowClear
						onChange={(v) => {
							setQuery((prev) => ({
								...prev,
								filterType: v,
								offset: 0,
							}));
						}}
						className="outer-shadow"
						placeholder="Type"
						options={[
							{
								label: "PDF",
								value: "PDF",
							},
							{
								label: "LINK",
								value: "LINK",
							},
						]}
					/>
				</Col>

				{query.filterOfficeId ? (
					<Col
						xs={{ span: 12 }}
						md={{ span: 6 }}
					>
						<FilterOffice
							value={query.filterOfficeId}
							onChange={_handleFilterOffice}
						/>
					</Col>
				) : (
					<Col
						xs={{ span: 12 }}
						md={{ span: 6 }}
					>
						<Select
							virtual={true}
							allowClear
							onChange={(v: string) => {
								const type = v && v.split(".")[1];

								if (type === "my_listing") {
									setQuery((prev) => ({
										...prev,
										filterUserId: undefined,
										filterMaskingByAgent: undefined,
										filterListingAgent: isAgent ? user.userId : undefined,
										offset: 0,
									}));
								} else if (type === "my_masking") {
									setQuery((prev) => ({
										...prev,
										filterUserId: undefined,
										filterMaskingByAgent: isAgent ? user.userId : undefined,
										filterListingAgent: undefined,
										offset: 0,
									}));
								} else {
									setQuery((prev) => ({
										...prev,
										filterUserId: isAgent ? user.userId : undefined,
										offset: 0,
									}));
								}
							}}
							className="outer-shadow"
							placeholder="Share"
							options={[
								{
									label: "Share by Me",
									value: `${user.userId}.my_masking`,
								},
								{
									label: "All by Me",
									value: `${user.userId}.my_listing`,
								},
							]}
						/>
					</Col>
				)}
				<Col
					xs={{ span: 12 }}
					md={{ span: 12 }}
				>
					<RangePicker
						placeholder={["Action start date", "Action end date"]}
						onChange={handleFilterActionDate}
						value={[dateRange[0] ? moment(dateRange[0]) : null, dateRange[1] ? moment(dateRange[1]) : null]}
					/>
				</Col>

				<Col
					xs={{ span: 12 }}
					md={{ span: 12 }}
				>
					<RangePicker
						placeholder={["Masking start date", "Masking end date"]}
						onChange={handleFilterMaskingDate}
						value={[
							maskingDateRange[0] ? moment(maskingDateRange[0]) : null,
							maskingDateRange[1] ? moment(maskingDateRange[1]) : null,
						]}
					/>
				</Col>
			</Filter>
			<Table
				loading={isLoading}
				columns={COLUMNS}
				dataSource={data}
				rowKey="listingMaskingHistoryId"
				pagination={false}
				scroll={{ x: "100%" }}
			/>
			<CustomPagination
				current={pagination.page}
				total={pagination.totalData}
				defaultPageSize={pagination.perPage}
				pageSize={pagination.perPage}
				onChange={changePage}
			/>
		</Space>
	);
};
export default HistoryMaskingTable;
